'use memo'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  addFinderPreference,
  updateFinderPreference,
  removeFinderPreference,
} from '@sportninja/common/services/player/finderSettingsService'
import type {
  FinderPreferenceInput,
  FinderSettings,
} from '@sportninja/common/features/profile/finderSettings/types'

interface AddVars {
  input: FinderPreferenceInput
}
interface UpdateVars extends AddVars {
  prefId: string
}

export const useFinderPreferenceMutations = (playerId: string) => {
  const queryClient = useQueryClient()
  // SN-8299 — every write answers with the whole settings envelope (one
  // preference per sport), so it goes straight into the cache.
  const onSaved = (settings: FinderSettings) => {
    queryClient.setQueryData(
      queryKeys.finderSettings.byPlayer(playerId),
      settings
    )
    // A preference edit changes how (and whether) this player appears in the
    // Player Finder — position, skill level, rate and available days are all
    // shown on the spare candidate cards, and position, gender and the card's
    // location (SN-8295) are server-side filters. Drop the cached spare lists so the finder can't serve a stale
    // view of this player.
    queryClient.invalidateQueries({
      queryKey: queryKeys.spareCandidates.all,
    })
  }

  const add = useMutation<FinderSettings, Error, AddVars>({
    mutationFn: ({ input }) => addFinderPreference(playerId, input),
    retry: 0,
    onSuccess: onSaved,
  })

  const update = useMutation<FinderSettings, Error, UpdateVars>({
    mutationFn: ({ prefId, input }) =>
      updateFinderPreference(playerId, prefId, input),
    retry: 0,
    onSuccess: onSaved,
  })

  const remove = useMutation<FinderSettings, Error, { prefId: string }>({
    mutationFn: ({ prefId }) => removeFinderPreference(playerId, prefId),
    retry: 0,
    onSuccess: onSaved,
  })

  return { add, update, remove }
}
